import type {
    InboxWorkflowCommitResult,
    InboxWorkflowState,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"

import type { PrintSelectionPreviewRow } from "../../../../../_shared/_logic/print-selection-preview"

export type LetterDeliveryCompletionOutput =
  | {
      actionCaseId: string
      letterDeliveryBatchItemId: string
      outcome: "delivered"
      deliveryMode: "manual" | "automatic"
      deliveredContactOrderStepId: string
      printJobId: string | null
      reasonCode?: string
    }
  | {
      actionCaseId: string
      letterDeliveryBatchItemId: string
      outcome: "not_required"
      reasonCode: string | null
      reasonLabel: string | null
    }

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const optionalString = (value: unknown): string | null => {
  const normalized = typeof value === "string" ? value.trim() : ""

  return normalized || null
}

export const parseLetterDeliveryCompletionOutput = (
  value: unknown,
  actionCaseId: string,
): LetterDeliveryCompletionOutput | null => {
  const stepOutput = asRecord(value)
  const output = asRecord(stepOutput.letter_delivery_completion ?? stepOutput)
  const letterDeliveryBatchItemId = optionalString(output.letter_delivery_batch_item_id)
  const outcome = optionalString(output.outcome)

  if (!actionCaseId || !letterDeliveryBatchItemId) return null

  if (outcome === "delivered") {
    const deliveryMode = optionalString(output.delivery_mode)
    const deliveredContactOrderStepId = optionalString(output.delivered_contact_order_step_id)

    if (
      (deliveryMode !== "manual" && deliveryMode !== "automatic") ||
      !deliveredContactOrderStepId
    ) {
      return null
    }

    return {
      actionCaseId,
      letterDeliveryBatchItemId,
      outcome,
      deliveryMode,
      deliveredContactOrderStepId,
      printJobId: optionalString(output.print_job_id),
      ...(optionalString(output.reason_code)
        ? { reasonCode: optionalString(output.reason_code)! }
        : {}),
    }
  }

  if (outcome === "not_required") {
    return {
      actionCaseId,
      letterDeliveryBatchItemId,
      outcome,
      reasonCode: optionalString(output.reason_code),
      reasonLabel: optionalString(output.reason_label),
    }
  }

  return null
}

export const letterDeliveryCompletionsFromWorkflow = (
  workflow: InboxWorkflowState,
): LetterDeliveryCompletionOutput[] =>
  workflow.items.flatMap((item) => {
    const step = item.steps.find(
      (candidate) => candidate.step_key === "deliver_appointment_letters",
    )
    const completion = parseLetterDeliveryCompletionOutput(step?.output, item.action_case.id)

    return completion ? [completion] : []
  })

export const letterDeliveryCompletionsFromReceipt = (
  receipt: InboxWorkflowCommitResult,
): LetterDeliveryCompletionOutput[] =>
  receipt.rows.flatMap((row) => {
    if (row.status.trim().toLowerCase() !== "success") return []

    const completion = parseLetterDeliveryCompletionOutput(row.output, row.action_case_id)

    return completion ? [completion] : []
  })

export const mergeLetterDeliveryCompletions = (
  current: readonly LetterDeliveryCompletionOutput[],
  incoming: readonly LetterDeliveryCompletionOutput[],
): LetterDeliveryCompletionOutput[] => {
  const byActionCaseId = new Map(current.map((completion) => [completion.actionCaseId, completion]))
  incoming.forEach((completion) => byActionCaseId.set(completion.actionCaseId, completion))

  return [...byActionCaseId.values()]
}

export const completionByActionCaseId = (
  completions: readonly LetterDeliveryCompletionOutput[],
): Record<string, LetterDeliveryCompletionOutput> =>
  Object.fromEntries(completions.map((completion) => [completion.actionCaseId, completion]))

export const distinctPersistedPrintJobIds = (
  completions: readonly LetterDeliveryCompletionOutput[],
): string[] =>
  Array.from(
    new Set(
      completions.flatMap((completion) =>
        completion.outcome === "delivered" && completion.printJobId ? [completion.printJobId] : [],
      ),
    ),
  )

export const projectCompletedPrintSelectionRows = (
  rows: readonly PrintSelectionPreviewRow[],
  completionsByActionCaseId: Readonly<Record<string, LetterDeliveryCompletionOutput>>,
): PrintSelectionPreviewRow[] =>
  rows.map((row) => {
    const actionCaseId = optionalString(row.action_case_id)
    const completion = actionCaseId ? completionsByActionCaseId[actionCaseId] : null

    if (!completion || completion.letterDeliveryBatchItemId !== row.letter_delivery_batch_item_id)
      return row

    if (completion.outcome === "not_required") {
      const detail =
        completion.reasonLabel ?? "Für diesen Terminbrief ist keine Zustellung erforderlich."

      return {
        ...row,
        printable:
          completion.reasonCode === "letter_delivery_ignored_by_user" ? false : row.printable,
        recommended_print: false,
        recommendation_reason_code: completion.reasonCode ?? "not_required",
        recommendation_text: detail,
        recommendation_tag_label: "Nicht erforderlich",
        recommendation_tag_color: "neutral",
        recommendation_short_reason: completion.reasonLabel ?? "Abgeschlossen",
        status_label:
          completion.reasonCode === "letter_delivery_ignored_by_user"
            ? "Bewusst ignoriert"
            : "Nicht erforderlich",
      }
    }

    if (completion.reasonCode === "manual_letter_downloaded") {
      return {
        ...row,
        recommended_print: false,
        recommendation_reason_code: "manual_letter_downloaded",
        recommendation_text:
          "Das PDF wurde heruntergeladen. Die tatsächliche Zustellung ist nicht bestätigt.",
        recommendation_tag_label: "Zustellung prüfen",
        recommendation_tag_color: "orange",
        recommendation_short_reason: "PDF heruntergeladen",
        status_label: "Zustellung nicht bestätigt",
      }
    }

    const modeLabel =
      completion.deliveryMode === "automatic" ? "Automatisch zugestellt" : "Händisch zugestellt"

    return {
      ...row,
      disabled_reason: row.printable
        ? null
        : (row.disabled_reason ?? "Terminbrief kann nicht mehr eindeutig rekonstruiert werden."),
      recommended_print: false,
      recommendation_reason_code: "delivered",
      recommendation_text: "Terminbrief wurde bereits zugestellt.",
      recommendation_tag_label: "Zugestellt",
      recommendation_tag_color: "emerald",
      recommendation_short_reason: modeLabel,
      status_label: "Zugestellt",
    }
  })
